import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibCheckbox } from '@chewy/kib-choices-react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Button, Grid, Paper } from '@mui/material'
import { makeStyles,styled  } from '@mui/styles'
import { ButtonWithLoader, type Nil, Text } from '@pbt/pbt-ui-components'

import { ConversationTransport } from '~/api/graphql/generated/types'
import { StatusError } from '~/components/elements/Status/Status'
import { MessageType } from '~/constants/communications'
import FeatureToggle from '~/constants/featureToggle'
import {
  createConversation,
  fetchConversationEmailPreview,
} from '~/store/actions/conversations'
import { getContact } from '~/store/duck/contacts'
import {
  useCreatedConversationsInfo,
  useIncludeMedicalRecord,
} from '~/store/hooks/conversations'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getAttachmentsToSend } from '~/store/reducers/conversationMessages'
import {
  getConversationEmailPreview,
  getConversationEmailPreviewIsReceiving,
  getConversationsError,
  getConversationsIsCreating,
} from '~/store/reducers/conversations'
import { getUser } from '~/store/reducers/users'
import type { ContactSlot } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import {
  DEFAULT_FIELDS_TO_VALIDATE,
  useNewConversationValidationForm,
} from '~/utils/useNewConversationValidationForm'

import ConversationMessageFormattingArea, {
  type ConversationMessageFormattingAreaHandle,
  type ConversationMessageFormattingAreaProps,
} from '../ConversationMessageFormattingArea'

import '@chewy/kib-choices-styles/dist/styles/kib-checkbox.css'
import styles from './NewMessage.module.scss'

const StyleSendButton = styled(ButtonWithLoader)(({ theme }) => ({
  width: 150,
  margin: theme.spacing(0, 2),
}))

const StyledDraftButton = styled(ButtonWithLoader)(({ theme }) => ({
  color: theme.colors.brandPrimary,
}))

const StyledBackButton = styled(Button)(() => ({ paddingLeft: 0 }))

const StyledBackArrow = styled(ArrowBackIcon)(({ theme }) => ({
  color: theme.colors.grayGray2,
}))

const useStyles = makeStyles(
  (theme) => ({
    messageFormattingArea: {
      padding: theme.spacing(2, 2, 3),
    },
  }),
  { name: 'NewMessage' },
)

enum ProcessingType {
  DRAFT = 'DRAFT',
  PLAIN = 'PLAIN',
}

interface NewMessageProps
  extends Omit<
    ConversationMessageFormattingAreaProps,
    'patientId' | 'clientId' | 'messageField'
  > {
  appointmentId: string | Nil
  contactSlot: ContactSlot
  handleBack: () => void
  handleConversationCreated: (isDraft: boolean) => void
  initialSubject?: string
}

const NewMessage = ({
  handleBack,
  handleConversationCreated,
  transport,
  contactSlot,
  initialSubject = '',
  appointmentId,
  ...rest
}: NewMessageProps) => {
  const conversationMessageFormattingAreaRef =
    useRef<ConversationMessageFormattingAreaHandle>(null)
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation('Common')

  const isCommunicationRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
  )
  const isConversationCreating = useSelector(getConversationsIsCreating)
  const conversationsError = useSelector(getConversationsError)
  const isEmailPreviewLoading = useSelector(
    getConversationEmailPreviewIsReceiving,
  )
  const emailTemplate = useSelector(getConversationEmailPreview)
  const { clientId, patientId, contactId } = contactSlot
  const client = useSelector(getUser(clientId))
  const contact = useSelector(getContact(contactId))
  const files = useSelector(getAttachmentsToSend)
  const [processingMessageType, setProcessingMessageType] =
    useState<ProcessingType>()
  const initialContacts = contact ? [contact] : []

  const isLoading = Boolean(
    isConversationCreating || isEmailPreviewLoading || processingMessageType,
  )
  const sendButtonText =
    transport === ConversationTransport.LogPhoneCall
      ? t('Common:LOG_ACTION')
      : t('Common:SEND_ACTION')

  const [includeHeaderAndFooter, setIncludeHeaderAndFooter] = useState(true)

  const toggleIncludeHeaderAndFooter = () =>
    setIncludeHeaderAndFooter(!includeHeaderAndFooter)

  const { shouldShowCheckbox: showMedicalCheckbox, defaultCheckboxValue } =
    useIncludeMedicalRecord()

  const {
    fields: { subject: subjectField, message: messageField, to, isMedical },
    validate,
    reset,
  } = useNewConversationValidationForm(
    { transport, client, contact },
    {
      initialSubject,
      initialMessage: '',
      initialIsMedicalMessage: defaultCheckboxValue,
    },
    {
      fieldsToValidate: [
        ...DEFAULT_FIELDS_TO_VALIDATE,
        ...(showMedicalCheckbox ? ['isMedical'] : []),
      ],
    },
  )

  useEffect(() => {
    reset()
  }, [clientId, patientId, contactId])

  const onConversationCreated = () => {
    handleConversationCreated(processingMessageType === ProcessingType.DRAFT)
    setProcessingMessageType(undefined)
  }

  const onConversationCreationFailure = () => {
    setProcessingMessageType(undefined)
  }

  const displayConversationCreationResult = useCreatedConversationsInfo({
    getIsConversationCreating: getConversationsIsCreating,
    onConversationCreationSuccess: onConversationCreated,
    onConversationCreationFailure,
    displayDefaultSuccessResult: false,
  })

  const getMessageType = (
    transportToHandle: ConversationTransport | Nil,
    processingMessageTypeToHandle: ProcessingType | Nil,
  ) =>
    transportToHandle === ConversationTransport.LogPhoneCall &&
    processingMessageTypeToHandle !== ProcessingType.DRAFT
      ? MessageType.INTERNAL_NOTE
      : MessageType.PLAIN

  const createConversationWithMessage = (
    messageBody: string,
    processingMessageTypeToCheck: ProcessingType | Nil,
  ) => {
    const recipients =
      conversationMessageFormattingAreaRef.current?.formRecipients()
    const title = subjectField.value
    const medicalInfo = showMedicalCheckbox
      ? { isMedical: isMedical.value }
      : {}
    const message = {
      body: messageBody,
      type: getMessageType(transport, processingMessageTypeToCheck),
      files,
      ...medicalInfo,
    }
    const isDraft = processingMessageTypeToCheck === ProcessingType.DRAFT
    const conversation = {
      transport,
      title,
      recipients,
      isDraft,
      eventId: appointmentId,
    }

    dispatch(createConversation(conversation, message))
    displayConversationCreationResult()
  }

  const fetchEmailTemplate = useCloseAfterCreation(() => {
    createConversationWithMessage(emailTemplate || '', processingMessageType)
  }, getConversationEmailPreviewIsReceiving)

  const handleSendMessage = (messageTypeToHandle: ProcessingType) => {
    setProcessingMessageType(messageTypeToHandle)
    if (validate()) {
      if (transport === ConversationTransport.Email && !emailTemplate) {
        dispatch(
          fetchConversationEmailPreview({
            includeHeaderAndFooter,
            content: messageField.value,
          }),
        )
        fetchEmailTemplate()
      } else {
        const messageBody =
          transport === ConversationTransport.Email
            ? emailTemplate
            : messageField.value
        createConversationWithMessage(messageBody, messageTypeToHandle)
      }
    } else {
      setProcessingMessageType(undefined)
    }
  }

  return (
    <Grid container direction="column">
      <ConversationMessageFormattingArea
        isShowPreview
        showAttachment
        classes={{
          messageFormattingArea: classes.messageFormattingArea,
        }}
        clientId={clientId}
        eventId={appointmentId}
        hideTo={transport === ConversationTransport.LogPhoneCall}
        includeHeaderAndFooter={includeHeaderAndFooter}
        isChirp={isCommunicationRightRailEnabled}
        messageField={messageField}
        minEditorHeight={150}
        patientId={patientId}
        ref={conversationMessageFormattingAreaRef}
        subjectField={subjectField}
        to={to}
        toInputProps={{ initialContacts }}
        toggleIncludeHeaderAndFooter={toggleIncludeHeaderAndFooter}
        transport={transport}
        {...rest}
      />

      {showMedicalCheckbox && (
        <KibCheckbox
          className={styles.includeMedicalCheckbox}
          label={isMedical.label}
          selectedValue={isMedical.value}
          onChange={(value) => isMedical.setValue(value)}
        />
      )}

      <Paper square elevation={3}>
        <Grid p={2}>
          {conversationsError && (
            <StatusError mb={1}>{conversationsError}</StatusError>
          )}
          <StyledBackButton onClick={handleBack}>
            <StyledBackArrow />
            <Text
              strong
              pl={1}
              sx={{ textTransform: 'none' }}
              textTransform="none"
              variant="lowAccent2"
            >
              {t('Common:BACK_ACTION')}
            </Text>
          </StyledBackButton>

          <StyleSendButton
            disabled={isLoading}
            loading={
              isLoading && processingMessageType === ProcessingType.PLAIN
            }
            onClick={() => handleSendMessage(ProcessingType.PLAIN)}
          >
            {sendButtonText}
          </StyleSendButton>
          {transport !== ConversationTransport.LogPhoneCall && (
            <StyledDraftButton
              color="secondary"
              disabled={isLoading}
              loading={
                isLoading && processingMessageType === ProcessingType.DRAFT
              }
              onClick={() => handleSendMessage(ProcessingType.DRAFT)}
            >
              {t('Common:SAVE_AS_DRAFT')}
            </StyledDraftButton>
          )}
        </Grid>
      </Paper>
    </Grid>
  )
}

export default NewMessage
